/**
 * A tela de etapas (F3.8) — a regra de edição, igual no painel e no app do
 * gestor.
 *
 * Mora aqui, e não em cada tela, porque as duas cópias divergiram na primeira
 * versão: uma casava a etapa nova pelo nome e perdia o tempo dela, as duas
 * resetavam o formulário a cada releitura, e "voltar ao padrão" tinha três
 * regras (a do `resetStageEntries` e uma por tela). Puro, sem React.
 */
// Subcaminho, e não o índice: o índice do `@mustauto/types` puxa o `zod`
// (address.ts), e o web-site no Docker instala só o próprio workspace.
import { resetStageEntries } from '@mustauto/types/modules';

/** Uma etapa como `GET /settings/stages` devolve (o que a tela precisa dela). */
export interface StageEditorSource {
  key: string;
  label: string;
  defaultLabel: string | null;
  hidden: boolean;
  photo: boolean;
  isPaintBooth: boolean;
  openOrders: number;
  hours: number | null;
}

/** Uma linha da tela. `key` ausente = etapa nova, que ganha a chave no servidor (a partir do nome). */
export interface StageEditorRow {
  /** Identidade estável na lista (React `key`): a chave, ou um id local da etapa nova. */
  uid: string;
  key?: string;
  label: string;
  hidden: boolean;
  photo: boolean;
  hours: number | null;
  defaultLabel: string | null;
  isPaintBooth: boolean;
  openOrders: number;
}

let newRowSeq = 0;

export function stageEditorRows(stages: readonly StageEditorSource[]): StageEditorRow[] {
  return stages.map((s) => ({
    uid: s.key,
    key: s.key,
    label: s.label,
    hidden: s.hidden,
    photo: s.photo,
    hours: s.hours,
    defaultLabel: s.defaultLabel,
    isPaintBooth: s.isPaintBooth,
    openOrders: s.openOrders,
  }));
}

/** Etapa nova, vazia — entra no fim das visíveis (a escondida fica embaixo, onde a tela a mostra). */
export function addStageEditorRow(rows: readonly StageEditorRow[]): { rows: StageEditorRow[]; uid: string } {
  const row: StageEditorRow = {
    uid: `new-${++newRowSeq}`,
    label: '',
    hidden: false,
    photo: false,
    hours: null,
    defaultLabel: null,
    isPaintBooth: false,
    openOrders: 0,
  };
  const firstHidden = rows.findIndex((r) => r.hidden);
  const next = firstHidden < 0 ? [...rows, row] : [...rows.slice(0, firstHidden), row, ...rows.slice(firstHidden)];
  return { rows: next, uid: row.uid };
}

/** O nome como o servidor o grava: sem espaço nas pontas nem espaço duplo. */
export function normalizeStageLabel(label: string): string {
  return label.trim().replace(/\s+/g, ' ');
}

/** "O que se salva em `PUT /settings/stages`" — mudou se isto mudou. */
export function stageEditorShape(rows: readonly StageEditorRow[]): string {
  return JSON.stringify(rows.map((r) => [r.key ?? null, normalizeStageLabel(r.label), r.hidden, r.photo]));
}

/** "O que se salva em `PUT /settings/stage-durations`" — mudou se isto mudou. */
export function durationEditorShape(rows: readonly StageEditorRow[]): string {
  return JSON.stringify(rows.map((r) => [r.key ?? normalizeStageLabel(r.label), r.hours]));
}

/**
 * O formulário acompanha o servidor só enquanto o gestor não mexeu nele:
 * voltar para a aba (releitura), outro gestor salvando ou o próprio "Salvar"
 * de um módulo trazem um `saved` novo, e trocar o formulário ali jogava fora
 * as edições sem aviso. Devolve as linhas que a tela deve mostrar.
 */
export function followSavedStageRows(
  current: StageEditorRow[],
  previousSaved: readonly StageEditorRow[],
  nextSaved: StageEditorRow[],
): StageEditorRow[] {
  const untouched =
    stageEditorShape(current) === stageEditorShape(previousSaved) &&
    durationEditorShape(current) === durationEditorShape(previousSaved);
  return untouched ? nextSaved : current;
}

/** O que vai no corpo de `PUT /settings/stages`. A etapa nova vai sem `key`. */
export function stageEditorInput(
  rows: readonly StageEditorRow[],
): { key?: string; label: string; hidden: boolean; photo: boolean }[] {
  return rows.map((r) => ({
    ...(r.key ? { key: r.key } : {}),
    label: normalizeStageLabel(r.label),
    hidden: r.hidden,
    photo: r.photo,
  }));
}

/**
 * Aplica a resposta do `PUT /settings/stages` às linhas: a chave que a etapa
 * nova ganhou e o nome como foi gravado. Pela POSIÇÃO, e não pelo nome — a
 * resposta vem na ordem que a tela mandou, e o servidor normaliza o nome
 * ("Pré  montagem" vira "Pré montagem"): casar pelo nome perdia o tempo da
 * etapa nova. A tela fica com essas linhas: se os tempos falharem depois, o
 * próximo "Salvar" manda só os tempos, e não a etapa nova outra vez.
 */
export function withServerStageKeys(
  rows: readonly StageEditorRow[],
  saved: readonly { key: string; label: string }[],
): StageEditorRow[] {
  return rows.map((r, i) => {
    const server = saved[i];
    if (!server) return r;
    return { ...r, key: server.key, uid: r.key ? r.uid : server.key, label: server.label };
  });
}

/**
 * O corpo de `PUT /settings/stage-durations` com `complete: true`: a lista
 * INTEIRA, escondidas incluídas — "Sem previsão" numa escondida apaga o tempo
 * dela. Etapa sem chave (o PUT de etapas falhou) fica de fora.
 */
export function stageEditorDurations(rows: readonly StageEditorRow[]): Record<string, number> {
  const durations: Record<string, number> = {};
  for (const r of rows) if (r.key && r.hours !== null) durations[r.key] = r.hours;
  return durations;
}

/**
 * "Voltar às etapas padrão" sobre as linhas da tela, pela regra única de
 * `resetStageEntries`: a lista padrão, com as criadas escondidas no fim (etapa
 * não se apaga). Os tempos ficam como estavam; a etapa nova ainda não salva
 * some, porque nunca existiu.
 */
export function resetStageEditorRows(
  module: string,
  defaults: readonly { key: string; label: string }[],
  rows: readonly StageEditorRow[],
): StageEditorRow[] {
  const byKey = new Map(rows.filter((r) => r.key).map((r) => [r.key!, r]));
  const defaultLabels = new Map(defaults.map((d) => [d.key, d.label]));
  const entries = resetStageEntries(
    module,
    rows.filter((r) => r.key).map((r) => ({ key: r.key!, label: r.label, hidden: r.hidden, photo: r.photo })),
  );
  return entries.map((e) => {
    const current = byKey.get(e.key);
    return {
      uid: e.key,
      key: e.key,
      label: e.label,
      hidden: e.hidden === true,
      photo: e.photo === true,
      hours: current?.hours ?? null,
      defaultLabel: defaultLabels.get(e.key) ?? null,
      isPaintBooth: current?.isPaintBooth ?? false,
      openOrders: current?.openOrders ?? 0,
    };
  });
}

/** O que impede salvar, dito como a tela diz — ou `null`. A API recusa o mesmo, com 400. */
export function stageEditorProblem(rows: readonly StageEditorRow[]): string | null {
  if (rows.some((r) => normalizeStageLabel(r.label) === '')) return 'Dê um nome a cada etapa.';
  const labels = rows.map((r) => normalizeStageLabel(r.label).toLocaleLowerCase('pt-BR'));
  if (new Set(labels).size !== labels.length) return 'Duas etapas com o mesmo nome.';
  if (!rows.some((r) => !r.hidden)) return 'Deixe pelo menos uma etapa visível.';
  return null;
}

/** A posição da etapa entre as VISÍVEIS (1 = primeira), ou `null` na escondida. */
export function stageEditorPosition(rows: readonly StageEditorRow[], index: number): number | null {
  const row = rows[index];
  if (!row || row.hidden) return null;
  return rows.slice(0, index + 1).filter((r) => !r.hidden).length;
}

/** Troca a etapa de lugar com a vizinha. Fora dos limites, a lista volta igual. */
export function moveStageEditorRow(rows: readonly StageEditorRow[], index: number, delta: -1 | 1): StageEditorRow[] {
  const target = index + delta;
  if (target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}
